import { Box, Typography, useTheme } from '@mui/material';
import { nanoid } from '@reduxjs/toolkit';
import { useNavigate, useLocation } from 'react-router-dom';

import { useChatAttachments, WelcomeMessage } from '@verantos-dev/feature-epi-chat';

import { ProjectLandingComposer } from './ProjectLandingComposer';
import { useWelcomeVariant } from '../hooks/useWelcomeVariant';
import { useCreateChatMutation } from '../lib/api/useProjectChats';

interface ChatPanelEmptyStateProps {
  projectId: string;
}

/**
 * Welcome shell for the chat panel when no ``chatId`` is selected (bare
 * ``/projects/:projectId`` URL, a detail route without a chat, or the
 * "+ New chat" action — which drops back to this no-chat state).
 *
 * OR-6123 (Batches A/B, surface F6) restructures this surface:
 *   - the static Research-Assistant **welcome greeting** renders in the
 *     thread (top-aligned, just below the chat header), with the composer
 *     **at the bottom** — matching the design;
 *   - the YOUR CHATS / ACTIVITY **tabs are gone** (the chat switcher is a
 *     separate dropdown surface — surface E); and
 *   - the shell is **ephemeral** — no DB row is persisted until the user's
 *     first message. The row is born on submit via the existing
 *     optimistic create-on-submit flow below.
 *
 * The welcome variant ("Hi!" vs "Hi again!") is decided by
 * {@link useWelcomeVariant} from the project's chats list; the greeting is
 * held until the list loads so the user never sees a variant flicker.
 *
 * Submitting in the composer optimistically mints a chat id client-side,
 * navigates to ``?chat=<uuid>`` immediately, and fires the POST in the
 * background. The BE accepts the client-supplied id so the navigation
 * lands on a real chat surface without waiting on the round-trip. The
 * first user-typed message is stashed (with a pre-minted client id) in
 * sessionStorage keyed by chatId. The session hydrate effect seeds an
 * optimistic bubble from that stash immediately, before the socket even
 * connects, so the welcome greeting and the message never flash away behind
 * a "Loading session…" skeleton; ``ActiveChatBody`` drains the same stash
 * once the socket subscribe ACKs and sends it for real via ``sendMessage``,
 * reusing the same id so the optimistic bubble and the real send dedupe to
 * one message. A 409 from the POST is treated as benign (duplicate id from a
 * double-submit).
 *
 * Preserves the current pathname so a user mid-scope (e.g. on a cohort
 * detail route) stays on that page with the new chat opening as a sidecar
 * instead of being thrown back to the project root.
 *
 * TODO: cleanup with research_assistant_gen_3 ff
 */
export const ChatPanelEmptyState = ({ projectId }: ChatPanelEmptyStateProps) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { mutateAsync: createChat, isPending } = useCreateChatMutation({ projectId });
  const { variant, isReady } = useWelcomeVariant(projectId);
  // The endpoint is project-scoped, so a file uploads before any chat row exists.
  const attachments = useChatAttachments(projectId);
  // Composer always spans the pane's full width — capping it stranded the
  // input box once drag-to-resize made `split` no longer a fixed 428px.
  // The greeting text keeps its own separate reading-column cap.

  // Mint a chat client-side and route there immediately (the optimistic create-on-submit flow
  // described above), returning the new chatId so callers can act on the live session. When
  // ``initialMessage`` is given it's stashed (with a pre-minted message id) BEFORE navigation,
  // so the session hydrate effect can seed an optimistic bubble immediately and
  // ``ActiveChatBody`` can drain the same id/text pair for the real send once the socket
  // subscribe ACKs.
  const mintChat = async (title: string, initialMessage?: string): Promise<string | null> => {
    const chatId = crypto.randomUUID().replace(/-/g, '');
    if (initialMessage !== undefined) {
      try {
        const staged = attachments.sendableAttachments;
        sessionStorage.setItem(
          `epi-chat-initial:${chatId}`,
          // Attachments ride the stash, so the file lands on the first message.
          JSON.stringify({
            id: nanoid(),
            text: initialMessage,
            ...(staged.length ? { attachments: staged } : {}),
          })
        );
      } catch {
        /* private browsing or quota — handled by the drain retry */
      }
    }
    navigate({ pathname: location.pathname, search: `?chat=${chatId}` });
    try {
      await createChat({ id: chatId, title });
    } catch (err) {
      const status = (err as { status?: number } | null)?.status;
      if (status !== 409) {
        console.error('Failed to create chat:', err);
        return null;
      }
    }
    return chatId;
  };

  const handleSubmit = async (text: string) => {
    // A file with no question is a valid opening; the title falls back to its name.
    const titleSource = text || attachments.sendableAttachments[0]?.filename || '';
    // Cap at the BE's own limit (200 chars) so the request doesn't fail
    // validation; each display surface handles its own ellipsis below that.
    const title = titleSource.length > 200 ? `${titleSource.slice(0, 197)}…` : titleSource;
    await mintChat(title, text);
  };

  return (
    <Box
      data-testid="chat-panel-empty-state"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        minHeight: 0,
        minWidth: 0,
        backgroundColor: 'background.default',
      }}
    >
      {/* Welcome thread — top-aligned so the greeting sits near the top of
          the panel instead of dropping to the bottom. Top padding of 16px
          (spacing token 4) matches the Figma spec's own measured gap
          between the chat header divider and the greeting text. */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          paddingX: 6,
          paddingTop: 4,
          paddingBottom: 1.5,
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 760 }}>
          {/* Hold the greeting until the chats list resolves so the user
              never sees a "Hi!" → "Hi again!" variant flicker. */}
          {isReady && <WelcomeMessage variant={variant} />}
        </Box>
      </Box>
      <Box
        sx={{
          flexShrink: 0,
          paddingX: theme.spacing(6),
          paddingY: theme.spacing(3),
          backgroundColor: 'background.paper',
          borderTop: (t) => `1px solid ${t.palette.divider}`,
        }}
      >
        <Box sx={{ width: '100%' }}>
          <ProjectLandingComposer
            onSubmit={handleSubmit}
            onAttach={attachments.attach}
            attachments={attachments.staged}
            onRemoveAttachment={attachments.remove}
            disabled={isPending}
          />
          {/* The `caption` variant's line-height (266% of its font-size) is
              tuned for multi-line reading and reserves a lot of invisible
              half-leading above/below the glyphs — for this single line it
              made the padding-based gaps above/below read as much bigger
              than the divider-to-box gap. Tightened to a single-line ratio
              so the explicit paddingTop below actually matches that gap. */}
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', lineHeight: 1.4, paddingTop: 3 }}
          >
            Research Assistant may make mistakes. Double-check all output.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};
